import {
    BIDashBoardCard,
    StyledBlock,
    StyledLinkText,
    StyledLinked,
    Wrapper,
} from "./BusinessIntelligencePage.styles";
import {
    GetPowerBIAllDashboards,
    GetPowerBIDashboardsByUserEmail,
    getPowerBIToken,
} from "features/business-intelligence/api/powerBI";
import PageWrapper, { LoadingWrapper } from "app/components/PageWrapper";
import React, { useCallback, useContext, useEffect, useState } from "react";

import Footer from "shared/components/layout/footer/Footer";
import HStack from "shared/components/layout/stack/HStack";
import Icon from "shared/components/utils/Icon";
import Loading from "shared/components/feedback/loading/Loading";
import PowerBIReport from "./components/PowerBIReport";
import PropTypes from "prop-types";
import ProvideFeedback from "shared/components/feedback/notification/ProvideFeedback";
import QuaternaryButton from "shared/components/inputs/button/QuaternaryButton";
import SPACING from "shared/config/spacing";
import SessionContext from "app/contexts/SessionContext";
import Tabs from "shared/components/navigation/tabs/Tabs";
import TertiaryButton from "shared/components/inputs/button/TertiaryButton";
import Text from "shared/components/data-display/text/Text";
import Tooltip from "shared/components/utils/tooltip/Tooltip";
import VStack from "shared/components/layout/stack/VStack";
import env from "react-dotenv";
import { groupBy } from "lodash";
import { useErrors } from "shared/hooks/errors/useErrors";
import { useSearchParams } from "react-router-dom";

const Card = ({ title, description, to, children, ...rest }) => (
    <StyledLinked to={to} {...rest}>
        <BIDashBoardCard>
            <VStack spacing={SPACING.Zero_25}>
                <HStack
                    align="center"
                    justify="space-between"
                    spacing={SPACING.One}
                >
                    <Text variant="Headline_3" color="Black">
                        {title}
                    </Text>
                    <Icon
                        style={{ flexShrink: 0, margin: "0px 0px 16px 0px" }}
                        width={16}
                        height={16}
                        name="link"
                        color="Black"
                    />
                </HStack>
                <Text variant="Descriptive_1_1" color="Gray_2">
                    {description}
                </Text>
            </VStack>
            {children}
        </BIDashBoardCard>
    </StyledLinked>
);

Card.propTypes = {
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    to: PropTypes.string.isRequired,
    children: PropTypes.node
};

const BIEmptyDashboardWrapper = () => {
    const [copy, setCopy] = useState("Copy email");

    const copyToClipboard = () => {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(env.POWERBI_EMAIL);
            setCopy("Copied to clipboard");
        }
    };
    return (
        <Wrapper>
            <VStack spacing="One">
                <HStack spacing="Zero_25">
                    <Icon size={16} name="error" color="Orange_1" />
                    <Text color="Black" variant="Body_2_2">
                        You do not have permission to view any dashboards.
                    </Text>
                </HStack>
                <Text variant="Body_2_1" color="Gray_2">
                    If you believe this is an error or if you have any
                    questions, please email the Business Intelligence
                    team
                    <Tooltip text={copy} enterDelay={0}>
                        <StyledBlock>
                            <StyledLinkText
                                variant="Body_2_1"
                                color="Black"
                                onClick={() => {
                                    copyToClipboard();
                                }}
                            >
                                &nbsp;{env.POWERBI_EMAIL}
                            </StyledLinkText>
                        </StyledBlock>
                    </Tooltip>
                </Text>
            </VStack>
        </Wrapper>
    );
};

const DashboardReportPages = ({ dashboard, onPageClick }) => (
    <>
        {dashboard.Reports.map((report) =>
            report.Pages.map((page) => (
                <QuaternaryButton
                    key={page.page_id}
                    label={page.page_name}
                    trailingIcon="caret-right"
                    labelColor="Gray_2"
                    onClick={(event) => onPageClick(event, dashboard, page.page_id)}
                />
            ))
        )}
    </>
);

DashboardReportPages.propTypes = {
    dashboard: PropTypes.object,
    onPageClick: PropTypes.func,
};

const BusinessIntelligencePage = () => {
    const BITitle = "Business Intelligence";
    const BIDescription =
        "Dashboards and reports to help you make informed data-driven decisions";
    const initialReportData = {
        token: "",
        dashboardId: "",
        reportId: "",
        pageName: "",
    };
    const session = useContext(SessionContext);
    const [reportData, setReportData] = useState(initialReportData);
    const [isAuthorize, setAuthorize] = useState(false);
    const [showReport, setShowReport] = useState(false);
    const [pbiToken, setPbiToken] = useState(null);
    const [userDashboards, setUserDashboards] = useState([]);
    const [userTabs, setUserTabs] = useState([]);
    const [dashboardsByTab, setDashboardsByTab] = useState([]);
    const [tabSelected, setTabSelected] = useState(null);
    const [searchParams] = useSearchParams();
    const [inProgress, setInProgress] = useState();
    const { handleError } = useErrors();
    const getBIToken = async () => {
        return await getPowerBIToken();
    }

    const getBIAPIData = useCallback(async () => {
        const userDashboards = Object.values(
            await GetPowerBIDashboardsByUserEmail(
                session.email.toLowerCase()
            )
        );
        const userDashboardsIds = userDashboards.map((x) => x.dashboard_id);

        if (userDashboards[0] === "-1") {
            setAuthorize(false);
            handleError({
                status: userDashboards[1],
                statusText: userDashboards[2]
            })
        }       

        const allDashboards = Object.values(
            await GetPowerBIAllDashboards()
        );
        const dashboardsToShow = allDashboards.filter((x) => {
            return userDashboardsIds.find((y) => y === x.dashboard_id);
        });

        dashboardsToShow.sort((a, b) => a.dashboard_name.localeCompare(b.dashboard_name));

        const groupedDashboards = groupBy(
            dashboardsToShow,
            "dashboard_group"
        );
        
        const mapTabs = Object.entries(groupedDashboards).map(
            ([key, value]) => {
                return { label: key, Icon: "", to: "" };
            }
        );

        if (mapTabs.length > 1) {
            mapTabs.push({ label: "All", Icon: "", to: "" });
        }

        const defaultTab = mapTabs[0]?.label;
        setUserTabs(mapTabs);
        setUserDashboards(dashboardsToShow);
        setDashboardsByTab(
            dashboardsToShow?.filter(
                (x) => x.dashboard_group === defaultTab
            )
        );
        setTabSelected(defaultTab);
        setInProgress(false);
    }, [session.email, setAuthorize, handleError, setUserTabs, setUserDashboards, setDashboardsByTab, setTabSelected, setInProgress]);

    useEffect(() => {
        let rId = searchParams.get("rId");
        let gId = searchParams.get("gId");
        if (rId && gId) {
            sessionStorage.setItem("BIrId", rId);
            sessionStorage.setItem("BIgId", gId);
        }
        setInProgress(true);
        const init = async () => {
            const pbiToken = await getBIToken();
            setPbiToken(pbiToken);
            if (pbiToken) {
                rId = sessionStorage.getItem("BIrId");
                gId = sessionStorage.getItem("BIgId");
                if (rId && gId) {
                    setReportData({
                        token: pbiToken,
                        dashboardId: gId,
                        reportId: rId,
                    });
                    setAuthorize(true);
                    setShowReport(true);
                    sessionStorage.removeItem("BIrId");
                    sessionStorage.removeItem("BIgId");
                    setInProgress(false);
                } else {
                    getBIAPIData();
                }
            }
        };
        init();
    }, [getBIAPIData, searchParams]);

    const clickTabAction = (data) => {
        let dashboardsByTab = userDashboards;
        if (data.label !== "All") {
            dashboardsByTab = userDashboards.filter((x) => x.dashboard_group === data.label);
        }
        setDashboardsByTab(dashboardsByTab);
        setTabSelected(data.label);
    };

    const dashboardClicked = (data, event) => {
        if (event.defaultPrevented) return;
        setReportData({
            token: pbiToken,
            dashboardId: data?.dashboard_id,
            reportId: data?.Reports[0]?.report_id,
        });
        setShowReport(true);
    };

    const pageClicked = (event, data, pageId) => {
        setReportData({
            token: pbiToken,
            dashboardId: data?.dashboard_id,
            reportId: data?.Reports[0]?.report_id,
            pageName: pageId,
        });
        event.preventDefault();
        setShowReport(true);
    };

    const showDashboards = async () => {
        await getBIAPIData();
        setAuthorize(true);
        setShowReport(false);
    };

    if (inProgress && isAuthorize) {
        return (
            <LoadingWrapper>
                <Loading text="Loading Page" />
            </LoadingWrapper>
        )
    }

    return showReport ? (
        <>
            <VStack
                spacing="One"
                style={{ padding: "60px 0px 10px 64px", width: "100%" }}
            >
                <HStack>
                    <TertiaryButton
                        label="Back to Business intelligence"
                        leadingIcon="caret-left"
                        onClick={showDashboards}
                    />
                </HStack>
            </VStack>
            <PowerBIReport reportData={reportData} />
            <Footer />
        </>
    ) : (
        <PageWrapper
            isAlreadyAuthorize={isAuthorize}
            description={BIDescription}
            title={BITitle}
        >
            <VStack spacing="One" style={{ paddingTop: "32px" }}>
                <Text variant="Headline_2" color="Gray_1">
                    Dashboards
                </Text>
                {userTabs.length > 0 && (
                    <Tabs
                        clickTab={clickTabAction}
                        selected={tabSelected}
                        tabs={userTabs}
                    />
                )}
                <HStack wrap={true}>
                    {dashboardsByTab.length > 0
                        ? dashboardsByTab.map((dashboard) => (
                            <Card
                                key={dashboard.dashboard_name}
                                title={dashboard.dashboard_name}
                                description={dashboard.dashboard_description}
                                onClick={(event) =>
                                    dashboardClicked(dashboard, event)
                                }
                            >
                                <HStack spacing={SPACING.Zero_5} wrap={true}>
                                    <DashboardReportPages
                                        dashboard={dashboard}
                                        onPageClick={pageClicked}
                                    />
                                </HStack>
                            </Card>
                        ))
                        : <BIEmptyDashboardWrapper />}
                </HStack>
                <VStack spacing="One" style={{ paddingTop: "64px" }}>
                    <HStack wrap={true}>
                        <Text variant="Headline_4" color="Gray_1">
                            {"Want to know more about dashboards?"}
                        </Text>
                    </HStack>
                    <HStack wrap={true}>
                        <TertiaryButton
                            trailingIcon="link"
                            label="Go to guide"
                            to={`https://docs.microsoft.com/en-us/learn/modules/explore-power-bi-service/2-find-content`}
                        />
                    </HStack>
                </VStack>
                <ProvideFeedback
                    title="Is This Helpful?"
                    description="Help us improve by telling what you think about the information available"
                    label={env.POWERBI_EMAIL}
                />
            </VStack>
        </PageWrapper>
    );
};

export default BusinessIntelligencePage;
